import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, FileDown } from 'lucide-react';
import { comparativoGeneralUrl } from '../data';
import IconRenderer from './IconRenderer';

export default function Header({
  activeTab,
  setActiveTab,
  activo,
  setActivo,
  navItems,
  data = [],
  perfilesCandidatos = {}
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const planCepeda = perfilesCandidatos?.cepeda?.planGobiernoUrl ?? '#';
  const planEspriella = perfilesCandidatos?.deLaEspriella?.planGobiernoUrl ?? '#';

  return (
    <>
      <header className="sticky top-0 z-30 flex flex-col bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="w-full flex justify-between items-center px-6 md:px-10 py-5">
          <div 
            onClick={() => setActiveTab('inicio')}
            className="flex items-center gap-2.5 cursor-pointer select-none"
          >
            <svg className="h-4.5 w-7 rounded-sm shadow-xs border border-slate-200/50 shrink-0" viewBox="0 0 3 2" aria-label="Bandera de Colombia">
              <rect width="3" height="1" fill="#FCD116" />
              <rect y="1" width="3" height="0.5" fill="#003893" />
              <rect y="1.5" width="3" height="0.5" fill="#CE1126" />
            </svg>
            <h1 className="font-bold text-xl tracking-tight text-slate-900">Elecciones 2026</h1>
          </div>
          <div className="flex items-center gap-3">
            <nav className="hidden lg:flex gap-2 text-sm font-medium text-slate-500 bg-slate-50 rounded-full p-1 border border-slate-100 shadow-sm">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`px-4 py-2 rounded-full transition-all ${
                    activeTab === item.id ? 'bg-white text-slate-900 shadow-sm' : 'hover:text-slate-900'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <button
              onClick={() => setIsMenuOpen(true)}
              className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white p-2 text-slate-700 shadow-sm lg:hidden"
              aria-label="Abrir menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="h-[2px] w-full bg-gradient-to-r from-amber-200/70 via-blue-200/70 to-red-200/70" />
      </header>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-slate-900/45 backdrop-blur-sm lg:hidden"
          >
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="ml-auto h-full w-full max-w-sm bg-white p-6 shadow-xl overflow-y-auto flex flex-col"
            >
              <div className="flex items-center justify-between shrink-0">
                <h2 className="text-lg font-bold">Menu</h2>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="rounded-full border border-slate-200 px-3 py-1 text-sm font-medium text-slate-600"
                >
                  Cerrar
                </button>
              </div>

              <div className="mt-7 shrink-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Navegacion</p>
                <div className="space-y-2">
                  {navItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        setActiveTab(item.id);
                        setIsMenuOpen(false);
                      }}
                      className={`w-full rounded-xl px-4 py-3 text-left text-sm font-semibold transition-colors ${
                        activeTab === item.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8 shrink-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Analisis de Politicas</p>
                <div className="space-y-2 max-h-[44vh] overflow-y-auto pr-1">
                  {data.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setActiveTab('propuestas');
                        setActivo(cat);
                        setIsMenuOpen(false);
                      }}
                      className={`w-full rounded-xl px-4 py-3 text-left text-sm font-medium transition-colors ${
                        activeTab === 'propuestas' && activo?.id === cat.id
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <IconRenderer name={cat.icono} className="h-4 w-4" />
                        {cat.titulo}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-100 mb-6 shrink-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4">Recursos y descargas</p>
                <div className="grid grid-cols-1 gap-3">
                  <a
                    href={planCepeda}
                    download
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 rounded-xl px-4 py-4 text-sm font-medium text-indigo-900 bg-indigo-50/60 border border-indigo-200 shadow-xs transition-colors hover:bg-indigo-100"
                  >
                    <FileDown className="h-5 w-5 text-indigo-500 shrink-0" />
                    <span className="truncate">Plan Iván Cepeda</span>
                  </a>

                  <a
                    href={planEspriella}
                    download
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 rounded-xl px-4 py-4 text-sm font-medium text-slate-800 bg-slate-100 border border-slate-300 shadow-xs transition-colors hover:bg-slate-200"
                  >
                    <FileDown className="h-5 w-5 text-slate-500 shrink-0" />
                    <span className="truncate">Plan Abelardo de la Espriella</span>
                  </a>

                  <a
                    href={comparativoGeneralUrl}
                    download
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 rounded-xl px-4 py-4 text-sm font-medium text-white bg-slate-800 border border-slate-600/30 shadow-xs transition-colors hover:bg-slate-700"
                  >
                    <FileDown className="h-5 w-5 text-slate-300 shrink-0" />
                    <span className="truncate">Comparativa General (PDF)</span>
                  </a>
                </div>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
